import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { MSym } from '@storeo/ui';
import { api } from '../lib/api';
import { DemoBanner, PlaceholderTag } from '../components/lumina/Placeholder';

const STATUS_LABEL: Record<string, string> = { active: 'Ativo', suspended: 'Suspenso' };
const STATUS_CLS: Record<string, string> = { active: 'bg-secondary/15 text-secondary', suspended: 'bg-error-container/25 text-error' };
const RISK_LABEL: Record<string, string> = { normal: 'Normal', watch: 'Observação', high: 'Alto' };
const RISK_CLS: Record<string, string> = {
  normal: 'bg-surface-container-highest text-outline',
  watch: 'bg-tertiary/15 text-tertiary',
  high: 'bg-error-container/25 text-error',
};
const STORE_STATUS_LABEL: Record<string, string> = {
  draft: 'Rascunho',
  published: 'Publicada',
  unpublished: 'Despublicada',
  suspended: 'Suspensa',
};

function money(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
function pct(v: number): string {
  return `${(v * 100).toFixed(2)}%`;
}
function when(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR');
}

/** Admin · Raio-X do parceiro — dados cadastrais + cada loja com GMV/comissão/chargeback.
 *  Consome `GET /api/admin/partners/{id}`. A seção "Telemetria de Edge" no fim é do
 *  protótipo Super Admin (Cloudflare TLS, chave PIX SPI, latência) — a Storeo não expõe
 *  isso hoje, fica marcada "exemplo". */
export function PartnerDetail() {
  const { id } = useParams<{ id: string }>();
  const partner = useQuery({
    queryKey: ['admin', 'partner', id],
    queryFn: () => api.partner(id!),
    enabled: !!id,
  });
  const p = partner.data;

  return (
    <>
      <Link to="/parceiros" className="self-start flex items-center gap-space-2xs text-on-surface-variant hover:text-on-surface font-label-md text-label-md">
        <MSym name="arrow_back" className="text-[18px]" /> Lojas & Subdomínios
      </Link>

      {partner.isLoading ? (
        <p className="font-body-sm text-body-sm text-on-surface-variant">Carregando…</p>
      ) : !p ? (
        <p className="font-body-sm text-body-sm text-on-surface-variant">Parceiro não encontrado.</p>
      ) : (
        <>
          <div className="relative overflow-hidden rounded-2xl bg-surface-container-low p-space-xl shadow-xl">
            <div className="relative z-10 flex flex-col gap-space-2xs">
              <h1 className="font-headline-lg text-headline-lg text-on-surface font-bold">{p.name}</h1>
              <p className="font-body-md text-body-md text-on-surface-variant">{p.email}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-space-sm">
            <div className="rounded-xl bg-surface-container-low p-space-sm shadow-md flex flex-col gap-space-3xs">
              <span className="font-label-sm text-label-sm text-outline uppercase">Status</span>
              <span className={`px-space-xs py-1 rounded-full font-label-sm text-label-sm font-bold inline-block self-start ${STATUS_CLS[p.status] ?? 'bg-surface-container-highest text-outline'}`}>
                {STATUS_LABEL[p.status] ?? p.status}
              </span>
            </div>
            <div className="rounded-xl bg-surface-container-low p-space-sm shadow-md flex flex-col gap-space-3xs">
              <span className="font-label-sm text-label-sm text-outline uppercase">Risco</span>
              <span className={`px-space-xs py-1 rounded-full font-label-sm text-label-sm font-bold inline-block self-start ${RISK_CLS[p.risk] ?? 'bg-surface-container-highest text-outline'}`}>
                {RISK_LABEL[p.risk] ?? p.risk}
              </span>
            </div>
            <div className="rounded-xl bg-surface-container-low p-space-sm shadow-md flex flex-col gap-space-3xs">
              <span className="font-label-sm text-label-sm text-outline uppercase">CPF</span>
              <span className="font-mono text-on-surface font-semibold">{p.cpf || '—'}</span>
            </div>
            <div className="rounded-xl bg-surface-container-low p-space-sm shadow-md flex flex-col gap-space-3xs">
              <span className="font-label-sm text-label-sm text-outline uppercase">Telefone</span>
              <span className="font-mono text-on-surface font-semibold">{p.phone || '—'}</span>
            </div>
            <div className="rounded-xl bg-surface-container-low p-space-sm shadow-md flex flex-col gap-space-3xs">
              <span className="font-label-sm text-label-sm text-outline uppercase">Cadastro</span>
              <span className="text-on-surface font-semibold">{when(p.createdAt)}</span>
            </div>
          </div>

          <section className="flex flex-col gap-space-md">
            <h2 className="font-headline-sm text-headline-sm text-on-surface font-bold">Lojas</h2>
            <div className="bg-surface-container-low rounded-2xl p-space-md shadow-lg overflow-hidden">
              {!p.stores.length ? (
                <p className="font-body-sm text-body-sm text-on-surface-variant p-space-md">Nenhuma loja criada ainda.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="text-outline font-label-sm text-label-sm uppercase tracking-wider bg-surface-container-lowest/60">
                        <th className="py-space-sm px-space-md rounded-l-lg">Loja</th>
                        <th className="py-space-sm px-space-sm">Status</th>
                        <th className="py-space-sm px-space-sm text-right">Pedidos pagos</th>
                        <th className="py-space-sm px-space-sm text-right">GMV</th>
                        <th className="py-space-sm px-space-sm text-right">Comissão</th>
                        <th className="py-space-sm px-space-md text-right rounded-r-lg">Chargeback</th>
                      </tr>
                    </thead>
                    <tbody className="text-body-sm font-body-sm">
                      {p.stores.map((store) => (
                        <tr key={store.id} className="hover:bg-surface-container/60 transition-colors">
                          <td className="py-space-md px-space-md">
                            <span className="font-bold text-on-surface">{store.name}</span>
                            <div className="text-outline font-label-sm text-label-sm">{store.subdomain}</div>
                          </td>
                          <td className="py-space-md px-space-sm text-on-surface-variant">
                            {(store.status && STORE_STATUS_LABEL[store.status]) ?? store.status}
                          </td>
                          <td className="py-space-md px-space-sm text-right font-mono text-on-surface">{store.paidOrders ?? 0}</td>
                          <td className="py-space-md px-space-sm text-right font-mono text-on-surface font-bold">{money(store.gmv ?? 0)}</td>
                          <td className="py-space-md px-space-sm text-right font-mono text-on-surface">{money(store.commission ?? 0)}</td>
                          <td className="py-space-md px-space-md text-right font-mono text-on-surface">{pct(store.chargebackRate ?? 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>

          <section className="flex flex-col gap-space-md">
            <DemoBanner>Telemetria de edge (Cloudflare, chave PIX SPI, latência) do protótipo Super Admin — a Storeo não expõe isso por parceiro hoje.</DemoBanner>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-md">
              <div className="bg-surface-container-low p-space-md rounded-xl flex flex-col gap-space-2xs">
                <span className="font-label-sm text-label-sm text-outline uppercase tracking-wider flex items-center gap-1">
                  <MSym name="shield" className="text-[16px] text-tertiary" /> Cloudflare TLS <PlaceholderTag />
                </span>
                <span className="font-label-lg text-label-lg font-bold text-on-surface">TLS 1.3 Strict</span>
              </div>
              <div className="bg-surface-container-low p-space-md rounded-xl flex flex-col gap-space-2xs">
                <span className="font-label-sm text-label-sm text-outline uppercase tracking-wider flex items-center gap-1">
                  <MSym name="key" className="text-[16px] text-primary" /> Chave PIX SPI <PlaceholderTag />
                </span>
                <span className="font-label-lg text-label-lg font-bold text-on-surface font-mono">pix-split-{p.id.slice(0, 8)}@storeo.app</span>
              </div>
            </div>
          </section>
        </>
      )}
    </>
  );
}
